import api from "@/lib/api/client";

// Long enough for a Drive scan/import of a full folder (see driveScan below).
const DRIVE_SYNC_TIMEOUT_MS = 5 * 60 * 1000;

export const documentApi = {
  classify: (employeeId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api
      .post(`/documents/employees/${employeeId}/classify`, formData, { headers: { "Content-Type": "multipart/form-data" } })
      .then((r) => r.data);
  },
  // aiMeta (optional) is whatever classify() above returned, carried through
  // so the same suggestion gets persisted with the document rather than
  // re-classifying the file a second time on upload.
  upload: (employeeId, docType, file, aiMeta) => {
    const formData = new FormData();
    formData.append("docType", docType);
    formData.append("file", file);
    if (aiMeta?.classified) {
      formData.append("aiConfidence", String(aiMeta.confidence));
      if (aiMeta.extractedFields) formData.append("aiExtractedFields", JSON.stringify(aiMeta.extractedFields));
      if (aiMeta.classifiedAt) formData.append("aiClassifiedAt", aiMeta.classifiedAt);
      if (aiMeta.containedDocTypes?.length > 1) formData.append("aiContainedDocTypes", JSON.stringify(aiMeta.containedDocTypes));
    }
    return api
      .post(`/documents/employees/${employeeId}/upload`, formData, { headers: { "Content-Type": "multipart/form-data" } })
      .then((r) => r.data);
  },
  list: (params) => api.get("/documents", { params }).then((r) => ({ items: r.data, meta: r.meta })),
  getById: (id) => api.get(`/documents/${id}`).then((r) => r.data),
  getDownloadUrl: (id) => api.get(`/documents/${id}/download-url`).then((r) => r.data),
  // For a document already on file that never had AI classification run
  // on upload -- re-reads it from storage and re-extracts, without asking
  // for the file again. Only meaningful for Aadhaar/PAN; the backend
  // rejects anything else. See document.service.js's own rescanIdentityNumber.
  rescanIdentityNumber: (id) => api.post(`/documents/${id}/rescan-identity-number`).then((r) => r.data),
  verify: (id) => api.post(`/documents/${id}/verify`).then((r) => r.data),
  reject: (id, reason) => api.post(`/documents/${id}/reject`, { reason }).then((r) => r.data),
  remove: (id) => api.delete(`/documents/${id}`).then((r) => r.data),
  requestReupload: (id, reason) => api.post(`/documents/${id}/request-reupload`, { reason }).then((r) => r.data),
  listMissingRequired: (employeeId) => api.get("/documents/missing-required", { params: { employeeId } }).then((r) => r.data),
  remindMissingDocument: (employeeId, docType, reason) =>
    api.post(`/documents/employees/${employeeId}/remind-missing`, { docType, reason }).then((r) => r.data),
  // One-time catch-up for employees hired before the offer-letter
  // auto-attach feature existed -- every new hire gets this automatically
  // now (employee.service.js's create()), this is just for the backlog.
  backfillOfferLetters: () => api.post("/documents/backfill-offer-letters").then((r) => r.data),
  getSummary: (employeeId) => api.get(`/documents/employees/${employeeId}/summary`).then((r) => r.data),

  // Bulk Document Import -- a sheet of many employees, each with their own
  // Drive folder, imported unattended (no per-file review screen; see
  // driveSync.service.js's autoImportFolder for what "unattended" means
  // here). HR-only.
  bulkImport: (file, belongTo) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("belongTo", belongTo);
    return api.post("/documents/bulk-import", formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  listBulkImports: (params) => api.get("/documents/bulk-import", { params }).then((r) => ({ items: r.data, meta: r.meta })),
  getBulkImport: (id) => api.get(`/documents/bulk-import/${id}`).then((r) => r.data),
  retryBulkImport: (id) => api.post(`/documents/bulk-import/${id}/retry`).then((r) => r.data),
  bulkImportErrorReport: (id) => api.get(`/documents/bulk-import/${id}/error-report`, { responseType: "blob" }).then((r) => r.data),
  // Powers the Employees template download -- one row per active employee,
  // with their current Drive folder link pre-filled if they already have
  // one connected (so re-uploading with a changed link is how HR updates
  // or replaces it), blank otherwise.
  employeesForBulkTemplate: () => api.get("/documents/bulk-import/employees-template").then((r) => r.data),

  // Sync from Google Drive (folder-link based)
  driveConnect: (employeeId, folderLink, includeNested) =>
    api.post(`/documents/employees/${employeeId}/drive/connect`, { folderLink, includeNested }).then((r) => r.data),
  driveDisconnect: (employeeId) => api.post(`/documents/employees/${employeeId}/drive/disconnect`).then((r) => r.data),
  driveStatus: (employeeId) => api.get(`/documents/employees/${employeeId}/drive/status`).then((r) => r.data),
  // Scan downloads and AI-classifies every new file one at a time, and
  // import uploads each one -- a folder of a handful of documents routinely
  // runs past the client's default 30s timeout, which surfaced as a bogus
  // "Cannot reach the server" while the backend was still working.
  // Fire-and-forget: the server responds immediately and imports whatever
  // it's confident about in the background, notifying the caller when done.
  driveSyncBackground: (employeeId) => api.post(`/documents/employees/${employeeId}/drive/sync`).then((r) => r.data),
  driveScan: (employeeId) =>
    api.post(`/documents/employees/${employeeId}/drive/scan`, null, { timeout: DRIVE_SYNC_TIMEOUT_MS }).then((r) => r.data),
  driveImport: (employeeId, selections) =>
    api.post(`/documents/employees/${employeeId}/drive/import`, { selections }, { timeout: DRIVE_SYNC_TIMEOUT_MS }).then((r) => r.data),
  // Org-wide manual "Sync now" -- re-scans every connected employee's
  // folder right now instead of waiting for the next scheduled sweep. See
  // features/settings/pages/OrganisationPage.jsx's Document Sync section.
  driveSyncNow: () => api.post("/documents/drive/sync-now").then((r) => r.data),
  // One batched call for every active Drive connection in the org -- used
  // by the Employees list page's Drive-link shortcut column. See
  // features/employees/pages/EmployeeListPage.jsx.
  driveConnections: () => api.get("/documents/drive/connections").then((r) => r.data),
};
